import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import LegacyTranslationBridge from "@/components/LegacyTranslationBridge";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardOverview from "@/pages/DashboardOverview";
import ProfileEditor from "@/pages/ProfileEditor";
import LinkManager from "@/pages/LinkManager";
import QRCodePage from "@/pages/QRCodePage";
import AttendancePage from "@/pages/AttendancePage";
import AttendanceScanPage from "@/pages/AttendanceScanPage";
import AttendancePassPage from "@/pages/AttendancePassPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import SettingsPage from "@/pages/SettingsPage";
import PublicProfile from "@/pages/PublicProfile";
import OnboardingWizard from "@/pages/OnboardingWizard";
import VariantSharePage from "@/pages/VariantSharePage";
import SuspendedPage from "@/pages/SuspendedPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import CheckEmailPage from "@/pages/CheckEmailPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ContactPage from "@/pages/ContactPage";
import HelpPage from "@/pages/HelpPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProfiles from "@/pages/admin/AdminProfiles";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children, requireVerified = true }) {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-loading-shell">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  // Site-QR workers use synthetic @workers.smart-qr.local emails and are marked
  // email_verified at join time. Never send them through the business check-email gate.
  if (user.attendance_worker) {
    if (
      !location.pathname.startsWith("/attendance/pass") &&
      !location.pathname.startsWith("/a/")
    ) {
      return <Navigate to="/attendance/pass" replace />;
    }
    return children;
  }
  if (requireVerified && !user.email_verified) {
    return <Navigate to="/check-email" replace />;
  }
  return children;
}

function WorkerPassEntry() {
  const { user, authLoading } = useAuth();
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-loading-shell">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user?.attendance_worker) {
    return <Navigate to="/attendance/pass" replace />;
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
}

function AdminProtectedRoute({ children }) {
  const { user, authLoading } = useAuth();
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-loading-shell">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" />;
  if (user.role !== "ADMIN") return <Navigate to="/dashboard" />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <LanguageSwitcher className="fixed bottom-4 right-4 z-[90]" />
        <LegacyTranslationBridge />
        <Routes>
          <Route path="/" element={<WorkerPassEntry />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/check-email" element={<ProtectedRoute requireVerified={false}><CheckEmailPage /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardOverview />} />
            <Route path="profile" element={<ProfileEditor />} />
            <Route path="links" element={<LinkManager />} />
            <Route path="qrcode" element={<QRCodePage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="help" element={<HelpPage />} />
          </Route>
          <Route path="/a/:token" element={<AttendanceScanPage />} />
          <Route
            path="/attendance/pass"
            element={
              <ProtectedRoute>
                <AttendancePassPage />
              </ProtectedRoute>
            }
          />
          <Route path="/p/:username" element={<PublicProfile />} />
          <Route path="/v/:token" element={<VariantSharePage />} />
          <Route path="/suspended" element={<SuspendedPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="profiles" element={<AdminProfiles />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="attendance" element={<AdminAttendance />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
